import React, { } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaWhatsapp, FaTimes } from 'react-icons/fa';

interface WhatsAppPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const WhatsAppPopup: React.FC<WhatsAppPopupProps> = ({ isOpen, onClose }) => {
  const whatsappNumbers = [
    {
      number: '+918219193005',
      label: 'Sales & Inquiries',
      description: 'Get quotes and product information'
    },
    {
      number: '+919520266015',
      label: 'Technical Support',
      description: 'Installation and maintenance support'
    }
  ];

  const handleWhatsAppClick = (number: string) => {
    const message = encodeURIComponent('Hello! I would like to know more about your solar energy solutions.');
    const whatsappUrl = `https://wa.me/${number.replace(/[^0-9]/g, '')}?text=${message}`;
    window.open(whatsappUrl, '_blank');
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with glassmorphism effect */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[99998] overflow-y-auto flex min-h-screen items-center justify-center"
            onClick={onClose}
          >
            {/* Popup content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ duration: 0.3, type: "spring", damping: 25, stiffness: 300 }}
              className="fixed bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 backdrop-blur-md border border-blackackunded-1xl p-6 sm:p-8 max-w-md w-full my-auto mx-auto shadow-2xl rounded-xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-200"
              >
                <FaTimes className="text-white text-lg" />
              </button>

              {/* Header */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
                  <FaWhatsapp className="text-3xl text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2">Chat with Us on WhatsApp</h2>
                <p className="text-white/70 text-sm">Choose the right department for faster assistance</p>
              </div>

              {/* WhatsApp buttons */}
              <div className="space-y-4">
                {whatsappNumbers.map((contact, index) => (
                  <motion.button
                    key={contact.number}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 + 0.2 }}
                    onClick={() => handleWhatsAppClick(contact.number)}
                    className="w-full p-4 bg-black/10 hover:bg-black/20 border border-black/20 rounded-xl transition-all duration-300 group hover:scale-[1.02] hover:shadow-lg"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center group-hover:bg-green-500/30 transition-colors duration-300">
                          <FaWhatsapp className="text-xl text-green-400" />
                        </div>
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="text-white font-semibold text-lg">{contact.label}</h3>
                        <p className="text-white/60 text-sm">{contact.description}</p>
                        <p className="text-green-400 text-sm font-medium mt-1">{contact.number}</p>
                      </div>
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-white/20 transition-colors duration-300">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Footer */}
              <div className="mt-6 text-center">
                <p className="text-white/50 text-xs">
                  We typically respond within a few minutes during business hours
                </p>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default WhatsAppPopup;